import { computed, Directive, ElementRef, inject, input, NgZone, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

/* Example usage:
    <img
    appParallax
    [parallaxOptions]="{
        enabled: true,
        strength: 50,
        strengthMap: { 0: 30, 640: 45, 992: 70, 1400: 100 },
        axis: 'y'
    }"
    />
*/

// Singleton lazy loader for GSAP + ScrollTrigger
let _gsapLoader: Promise<{ gsap: any; ScrollTrigger: any }> | null = null;
function loadGsap() {
  if (!_gsapLoader) {
    _gsapLoader = Promise.all([import('gsap'), import('gsap/ScrollTrigger')]).then(([core, plugin]) => {
      const gsap = (core as any).gsap || (core as any).default || core;
      const ScrollTrigger = (plugin as any).ScrollTrigger || (plugin as any).default || plugin;
      if (!(gsap as any).ScrollTrigger) gsap.registerPlugin(ScrollTrigger);
      return { gsap, ScrollTrigger };
    });
  }
  return _gsapLoader;
}

export interface ParallaxOptions {
  enabled: boolean; // toggle parallax on/off
  strength: number; // half travel (from -strength to +strength)
  strengthMap?: Record<number | string, number>; // optional responsive map (min-width in px => strength)
  axis: 'y' | 'x'; // scroll axis
  scaleOverride?: number | null; // optional fixed scale (otherwise calculated based on strength and image size)
  rootMargin: string; // IntersectionObserver rootMargin
  respectReducedMotion: boolean; // disable if user has "reduced motion" setting enabled
}

const DEFAULTS: ParallaxOptions = {
  enabled: true,
  strength: 40,
  axis: 'y',
  rootMargin: '0px 0px 400px',
  respectReducedMotion: true,
  scaleOverride: null,
};

@Directive({
  selector: '[appParallax]',
  standalone: true,
})
export class ParallaxDirective implements OnDestroy {
  private el = inject(ElementRef<HTMLElement>);
  private zone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  // Single options input
  parallaxOptions = input<Partial<ParallaxOptions>>({});

  private io?: IntersectionObserver;
  private triggerInstance?: any;
  private gsapTween?: any;
  private inited = false;
  private resizeListener?: () => void;
  private currentStrength?: number;

  options = computed<ParallaxOptions>(() => {
    return { ...DEFAULTS, ...this.parallaxOptions() };
  });

  constructor() {
    if (!this.isBrowser) return;
    this.io = new IntersectionObserver(this.handleIntersect, {
      root: null,
      rootMargin: this.options().rootMargin,
      threshold: [0, 0.01, 0.1, 0.5, 1],
    });
    this.io.observe(this.el.nativeElement);
  }

  private resolveStrength(): number {
    const map = this.options().strengthMap;
    if (!map) return this.options().strength;
    let width = 0;
    try {
      width = window.innerWidth;
    } catch {}
    const entries = Object.entries(map).map(([k, v]) => [Number(k), v as number]) as [number, number][];
    entries.sort((a, b) => a[0] - b[0]);
    let chosen = this.options().strength;
    for (const [bp, val] of entries) if (width >= bp) chosen = val;
    return chosen;
  }

  private handleIntersect = (entries: IntersectionObserverEntry[]) => {
    if (!this.options().enabled) return;
    for (const e of entries) {
      if (e.isIntersecting) {
        this.init();
        break;
      }
    }
  };

  private async init() {
    if (this.inited || !this.isBrowser) return;

    if (this.options().respectReducedMotion && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const el = this.el.nativeElement as HTMLElement;
    this.inited = true;

    const ensureLoaded = () =>
      new Promise<void>(resolve => {
        if (el.tagName === 'IMG' && !(el as HTMLImageElement).complete) {
          el.addEventListener('load', () => resolve(), { once: true });
        } else resolve();
      });
    try {
      await ensureLoaded();
      const { gsap } = await loadGsap();
      const axis = this.options().axis;
      const strength = this.resolveStrength();
      this.currentStrength = strength;

      // scale calc
      let scale = 1;
      const dim = axis === 'y' ? (el as any).naturalHeight || el.clientHeight : (el as any).naturalWidth || el.clientWidth;
      if (dim > 0) {
        const neededExtra = (strength * 2) / dim;
        const autoScale = 1 + neededExtra;
        scale = this.options().scaleOverride ?? autoScale;
      } else if (typeof this.options().scaleOverride === 'number') {
        scale = this.options().scaleOverride as number;
      }

      const baseFrom: any = { scale, willChange: 'transform' };
      const baseTo: any = { scale, ease: 'none' };

      this.zone.runOutsideAngular(() => {
        const buildTween = (s: number) => {
          const fromVars = { ...baseFrom } as any;
          const toVars = { ...baseTo } as any;
          if (axis === 'y') {
            fromVars.y = -s;
            toVars.y = s;
          } else {
            fromVars.x = -s;
            toVars.x = s;
          }
          if (this.gsapTween) {
            try {
              this.gsapTween.scrollTrigger?.kill();
              this.gsapTween.kill();
            } catch {}
          }
          const tween = gsap.fromTo(el, fromVars, {
            ...toVars,
            scrollTrigger: {
              trigger: el.closest('figure') || el,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
              onLeave: () => (el.style.willChange = ''),
              onLeaveBack: () => (el.style.willChange = ''),
            },
          });
          this.triggerInstance = tween.scrollTrigger;
          this.gsapTween = tween;
        };
        buildTween(strength);
        if (this.options().strengthMap && !this.resizeListener) {
          const onResize = () => {
            const newStrength = this.resolveStrength();
            if (newStrength === this.currentStrength) return;
            this.currentStrength = newStrength;
            buildTween(newStrength);
          };
          window.addEventListener('resize', onResize, { passive: true });
          this.resizeListener = () => window.removeEventListener('resize', onResize);
        }
      });
    } catch (err) {
      console.warn('ParallaxDirective init failed', err);
    } finally {
      if (this.io) {
        this.io.disconnect();
        this.io = undefined;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.io) this.io.disconnect();
    if (this.triggerInstance) {
      try {
        this.triggerInstance.kill();
      } catch {}
      this.triggerInstance = undefined;
    }
    if (this.gsapTween) {
      try {
        this.gsapTween.kill();
      } catch {}
      this.gsapTween = undefined;
    }
    if (this.resizeListener) {
      this.resizeListener();
      this.resizeListener = undefined;
    }
  }
}
